import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseChartDirective } from 'ng2-charts';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomerStatsCardComponent } from '../../../components/customer-stats-card/customer-stats-card.component';
import { ServiceOptinCardComponent } from '../../../components/service-optin-card/service-optin-card.component';
import { NewVsReturningCardComponent } from '../../../components/new-vs-returning-card/new-vs-returning-card.component';
import { DashboardStatData, ServiceOptinStatData, NewVsReturningData } from '../../../shared/types';
import { MobileDetectionMixin } from '../../../shared/mixins/mobile-detection.mixin';
import {
  DateRange,
  DateRangePickerComponent,
} from '../../../shared/components/date-range-picker/date-range-picker.component';

/**
 * CustomerTabComponent - Presents customer-focused dashboard cards and charts (New vs Returning, Visit Frequency, Monthly Acquisition, and service opt-ins) and handles empty-state logic and period/location controls.
 * @component
 * @selector app-customer-tab
 * @example
 *   <app-customer-tab
 *     [customerDashboardStats]="stats"
 *     [newVsReturningData]="newVsReturning"
 *     [newVsReturningTrendData]="trendData"
 *     [selectedPeriod]="'30d'"
 *     (selectPeriod)="onPeriodSelect($event)"
 *     (selectLocation)="onLocationSelect($event)">
 *   </app-customer-tab>
 *
 * Inputs:
 * @input {DashboardStatData[]} customerDashboardStats - Array of summary stat entries shown in the customer stats cards.
 * @input {NewVsReturningData} newVsReturningData - Aggregate new vs returning customer counts and metadata for the summary card.
 * @input {ServiceOptinStatData[]} serviceOptinStats - Service opt-in statistics displayed in the opt-in card.
 * @input {any} newVsReturningTrendData - Chart.js-style dataset object for the New vs Returning trend chart.
 * @input {any} newVsReturningTrendOptions - Chart options for the New vs Returning trend chart.
 * @input {any} visitFrequencyData - Chart.js-style dataset object for the Visit Frequency chart.
 * @input {any} visitFrequencyOptions - Chart options for the Visit Frequency chart.
 * @input {any} monthlyAcquisitionData - Chart.js-style dataset object for the Monthly Acquisition chart.
 * @input {any} monthlyAcquisitionOptions - Chart options for the Monthly Acquisition chart.
 * @input {any} customerStatsData - Additional metadata about customer presence (used to decide Figma empty state).
 * @input {string} selectedPeriod - Currently selected period identifier for dropdown control (e.g. '7d', '30d').
 * @input {boolean} isDropdownOpen - Whether the period dropdown is open.
 * @input {Array<{ value: string; label: string }>} periodOptions - Available period selection options.
 * @input {string} selectedLocation - Currently selected location identifier.
 * @input {string} selectedLocationLabel - Human-readable label for the selected location.
 * @input {boolean} isLocationDropdownOpen - Whether the location dropdown is open.
 * @input {Array<{ value: string; label: string }>} locationOptions - Available location selection options.
 * @input {boolean} isInitialLoad - True when the tab is loaded for the first time; affects empty-state behavior.
 * @input {boolean} hasAppliedFilters - True when the user has applied filters; affects whether individual empty states are shown.
 *
 * Outputs:
 * @output {EventEmitter<void>} toggleDropdown - Emits when the period dropdown open/close toggle is requested.
 * @output {EventEmitter<string>} selectPeriod - Emits a period identifier when the user selects a period option.
 * @output {EventEmitter<void>} toggleLocationDropdown - Emits when the location dropdown open/close toggle is requested.
 * @output {EventEmitter<string>} selectLocation - Emits a location identifier when the user selects a location.
 *
 * Key methods:
 * - hasNewVsReturningTrendData(): boolean - Returns true if the New vs Returning trend datasets contain any non-zero values.
 * - isNewVsReturningTrendDataEmpty(): boolean - Returns true if all New vs Returning trend datasets are missing or contain only zeros.
 * - hasVisitFrequencyData(): boolean - Returns true if the Visit Frequency dataset contains any non-zero values.
 * - isVisitFrequencyDataEmpty(): boolean - Returns true if the Visit Frequency dataset is missing or contains only zeros.
 * - hasMonthlyAcquisitionData(): boolean - Returns true if any Monthly Acquisition datasets contain non-zero values.
 * - isMonthlyAcquisitionDataEmpty(): boolean - Returns true if Monthly Acquisition datasets are missing or contain only zeros.
 * - isCustomerStatsDataEmpty(): boolean - Returns true if customerDashboardStats is empty or all stat values are zero.
 * - isNewVsReturningDataEmpty(): boolean - Returns true if both new and returning customer counts are zero or missing.
 * - isServiceOptinStatsDataEmpty(): boolean - Returns true if service opt-in stats are empty or all values are zero.
 * - isAllDataEmpty(): boolean - Convenience method returning true when all chart data sources (trend, visit frequency, monthly acquisition) are empty.
 * - shouldShowEmptyState(): boolean - Determines whether to show the global Figma-style empty state based on customerStatsData.
 * - shouldShowIndividualEmptyStates(): boolean - Determines whether to show per-chart empty states (depends on isInitialLoad and hasAppliedFilters).
 *
 * Lifecycle / behavior notes:
 * - The component is a standalone Angular component that receives all data via @Input properties and emits user interactions via @Output events.
 * - No explicit lifecycle hooks are implemented in this class; empty-state and UI visibility behavior is driven by the input values (notably isInitialLoad and hasAppliedFilters).
 * - Extends a MobileDetectionMixin to adapt behavior/presentation for mobile devices.
 */
@Component({
  selector: 'app-customer-tab',
  standalone: true,
  imports: [
    CommonModule,
    BaseChartDirective,
    MatTooltipModule,
    CustomerStatsCardComponent,
    ServiceOptinCardComponent,
    NewVsReturningCardComponent,
    DateRangePickerComponent,
  ],
  templateUrl: './customer-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../../dashboard.component.scss',
})
export class CustomerTabComponent extends MobileDetectionMixin {
  @Input() customerDashboardStats: DashboardStatData[] = [];
  @Input() newVsReturningData!: NewVsReturningData;
  @Input() serviceOptinStats: ServiceOptinStatData[] = [];

  @Input() newVsReturningTrendData: any;
  @Input() newVsReturningTrendOptions: any;
  @Input() visitFrequencyData: any;
  @Input() visitFrequencyOptions: any;
  @Input() monthlyAcquisitionData: any;
  @Input() monthlyAcquisitionOptions: any;
  @Input() customerStatsData: any;

  // Added to mirror RevenueTab dropdown controls
  @Input() selectedPeriod!: string;
  @Input() selectedPeriodLabel = '';
  @Input() isDropdownOpen = false;
  @Input() periodOptions: { value: string; label: string }[] = [];
  @Input() customDateRange: DateRange = { startDate: null, endDate: null };
  @Input() selectedLocation = '';
  @Input() selectedLocationLabel = '';
  @Input() isLocationDropdownOpen = false;
  @Input() locationOptions: { value: string; label: string }[] = [];
  @Input() isInitialLoad = true; // Track if this is initial page load
  @Input() hasAppliedFilters = false; // Track if filters have been applied

  @Output() toggleDropdown = new EventEmitter<void>();
  @Output() selectPeriod = new EventEmitter<string>();
  @Output() dateRangeApply = new EventEmitter<{ period: string; customRange?: DateRange }>();
  @Output() closePeriodDropdown = new EventEmitter<void>();
  @Output() toggleLocationDropdown = new EventEmitter<void>();
  @Output() selectLocation = new EventEmitter<string>();

  // Helper methods for New vs Returning Trend chart
  hasNewVsReturningTrendData(): boolean {
    if (
      !this.newVsReturningTrendData?.datasets ||
      this.newVsReturningTrendData.datasets.length === 0
    ) {
      return false;
    }

    // Check if any dataset has non-zero data
    return this.newVsReturningTrendData.datasets.some((dataset: any) => {
      const data = dataset?.data;
      return data && data.length > 0 && !data.every((value: number) => value === 0);
    });
  }

  isNewVsReturningTrendDataEmpty(): boolean {
    if (
      !this.newVsReturningTrendData?.datasets ||
      this.newVsReturningTrendData.datasets.length === 0
    ) {
      return true;
    }

    // Check if all datasets have only zero data
    return this.newVsReturningTrendData.datasets.every((dataset: any) => {
      const data = dataset?.data;
      return !data || data.length === 0 || data.every((value: number) => value === 0);
    });
  }

  // Helper methods for Visit Frequency chart
  hasVisitFrequencyData(): boolean {
    const data = this.visitFrequencyData?.datasets?.[0]?.data;
    return data && data.length > 0 && !data.every((value: number) => value === 0);
  }

  isVisitFrequencyDataEmpty(): boolean {
    const data = this.visitFrequencyData?.datasets?.[0]?.data;
    return !data || data.length === 0 || data.every((value: number) => value === 0);
  }

  // Helper methods for Monthly Acquisition chart
  hasMonthlyAcquisitionData(): boolean {
    if (
      !this.monthlyAcquisitionData?.datasets ||
      this.monthlyAcquisitionData.datasets.length === 0
    ) {
      return false;
    }

    // Check if any dataset has non-zero data
    return this.monthlyAcquisitionData.datasets.some((dataset: any) => {
      const data = dataset?.data;
      return data && data.length > 0 && !data.every((value: number) => value === 0);
    });
  }

  isMonthlyAcquisitionDataEmpty(): boolean {
    if (
      !this.monthlyAcquisitionData?.datasets ||
      this.monthlyAcquisitionData.datasets.length === 0
    ) {
      return true;
    }

    // Check if all datasets have only zero data
    return this.monthlyAcquisitionData.datasets.every((dataset: any) => {
      const data = dataset?.data;
      return !data || data.length === 0 || data.every((value: number) => value === 0);
    });
  }

  // Check if stats data is empty
  isCustomerStatsDataEmpty(): boolean {
    return (
      !this.customerDashboardStats ||
      this.customerDashboardStats.length === 0 ||
      this.customerDashboardStats.every(
        stat => stat.value === '0' || parseInt(stat.value, 10) === 0
      )
    );
  }

  // Check if new vs returning data is empty
  isNewVsReturningDataEmpty(): boolean {
    return (
      !this.newVsReturningData ||
      (parseInt(this.newVsReturningData.newCustomers.count, 10) === 0 &&
        parseInt(this.newVsReturningData.returningCustomers.count, 10) === 0)
    );
  }

  // Check if service opt-in stats data is empty
  isServiceOptinStatsDataEmpty(): boolean {
    return (
      !this.serviceOptinStats ||
      this.serviceOptinStats.length === 0 ||
      this.serviceOptinStats.every(stat => stat.value === '0' || parseInt(stat.value, 10) === 0)
    );
  }

  // Check if all data sources are empty
  isAllDataEmpty(): boolean {
    return (
      this.isNewVsReturningTrendDataEmpty() &&
      this.isVisitFrequencyDataEmpty() &&
      this.isMonthlyAcquisitionDataEmpty()
    );
  }

  // Helper method to determine if we should show the Figma empty state
  shouldShowEmptyState(): boolean {
    return this.customerStatsData?.is_customer_found;
  }

  // Helper method to determine if we should show individual chart empty states
  shouldShowIndividualEmptyStates(): boolean {
    return this.hasAppliedFilters || !this.isInitialLoad;
  }

  trackByValue(_: number, option: { value: string }): string {
    return option.value;
  }
  trackByStatTitle(_: number, stat: DashboardStatData): string {
    return stat.title;
  }
  trackByIndex(index: number): number {
    return index;
  }
}
